import { EChartsOption } from 'echarts';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';

export const GLOBAL_CHART_OPTION: EChartsOption = {
    animation: false,
    grid: {
        left: '1%',
        right: '1%',
        top: '3%',
        bottom: '1%',
        containLabel: true,
    },
    legend: { show: false },
    toolbox: {
        show: false,
        feature: {
            // saveAsImage: { show: true, title: 'Save as image' },
            dataView: { show: true, title: 'Data view' },
        },
    },
};

export const GLOBAL_LINE_CHART_OPTION: EChartsOption = {
    linestyle: { width: 1 },
};

export const GLOBAL_TIME_XAXIS_OPTION: EChartsOption = {
    xAxis: {
        type: 'time',
        boundaryGap: ['2%', '2%'],
        axisTick: {
            show: true,
            // @ts-ignore
            alignWithLabel: true,
        },
        axisLabel: {
            hideOverlap: true,
        },
    },
};

export const GLOBAL_TIME_YAXIS_OPTION: EChartsOption = {
    yAxis: {},
};

export const GLOBAL_VALUE_XAXIS_OPTION: EChartsOption = {
    xAxis: { type: 'value' },
};
/**
 * Flattens property with date
 * Converts datetime property to ISO string format
 * @param data
 */
export function bq2echartsformat(
    data: any[],
    options?: {
        /**
         * Formatter for datetime values
         * @param ts
         * @returns {string}
         */
        datetimeFormatter?: (ts: number) => string;
        /**
         * Key of datetime property
         * @default 'datetime'
         */
        datetimeKey?: string;
    }
) {
    // defaults
    if (!options) {
        options = {};
    }
    options.datetimeKey = options?.datetimeKey ?? 'datetime';

    data.map((row: any) => {
        Object.keys(row).map((key) => {
            if (key == 'date') {
                if ('value' in row[key]) {
                    // bq sends date as object with value property
                    row[key] = row[key].value;
                } else {
                    throw new Error(
                        'Unexpected date format in row: ' + row.date
                    );
                }
            } else if (key == options!.datetimeKey) {
                var ts: number;
                if (typeof row[key] === 'number') {
                    // assume it is unix timestamp in seconds
                    row[key] = new Date(row[key] * 1000).toISOString();
                } else if (typeof row[key] === 'string') {
                    // check if it can be converted to Number
                    const n = Number(row[key]);
                    if (!isNaN(n)) {
                        // if it is number in string format
                        row[key] = new Date(n * 1000).toISOString();
                    } else {
                        // should be ISO string format, try to convert to Date and back to ISO string
                        const d = new Date(row[key]);
                        row[key] = d.toISOString();
                    }
                } else {
                    throw new Error(`Unsupported datetime format: ${row[key]}`);
                }
            } else if (key == 'split_group') {
                row.split_group = row.split_group ?? '-';
            }
        });
    });
    // if date in row then filter out rows with null date
    data = data.filter((row: any) => row.date !== null);
}

export function composeBaseDataset(
    data: any[],
    context?: IQueryContext
): EChartsOption {
    // console.log({ msg: "composeBaseDataset", data, context });
    const dataset: EChartsOption['dataset'] = {
        id: 'dataset_raw',
        source: data,
    };
    return { dataset: [dataset] };
}

/**
 * Composes a base series object for ECharts.
 *
 * @param xLabel - The label for the x-axis.
 * @param yLabel - The label for the y-axis.
 * @param seriesName - The name of the series.
 * @param seriesType - The type of the series. Can be either 'line' or 'bar'. Defaults to 'line'.
 *
 * @returns An object that represents a base series for ECharts.
 */
export function composeBaseSeries(
    xLabel: string,
    yLabel: string,
    seriesName: string,
    seriesType: 'line' | 'bar' = 'line'
): EChartsOption {
    return {
        series: [
            {
                type: seriesType,
                showSymbol: false,
                name: seriesName,
                encode: { x: xLabel, y: yLabel },
                lineStyle: { width: 1 },
                color: ChartColorService.getDefaultColor(),
            },
        ],
    };
}

/** Composes set of datasets for each distinct value in split_group.
 * Assumes that the dataset has a column named `split_group`.
 * Assumes that default dataset id is `dataset_raw`.
 *
 * @param distinctValues - Array of distinct values to create a dataset from.
 */
export function composeSplitValueDataset(
    distinctValues: string[]
): EChartsOption['dataset'][] {
    return distinctValues.map((splitValue): EChartsOption['dataset'] => {
        return {
            id: splitValue,
            fromDatasetId: 'dataset_raw',
            transform: {
                type: 'filter',
                config: { dimension: 'split_group', value: splitValue },
            },
        };
    });
}

/**
 * Composes a new series by splitting based on distinct values.
 *
 * @param distinctValues - Array of distinct string values to split series.
 * @param series - Array of EChartsOptions.
 * @param colorGetter - Callback to generate a color for each split series.
 *                      Receives the current split value as an argument.
 * @param nameGetter - Optional callback to generate a new name for each split series.
 *                       Receives the original series name and the current split value as arguments.
 *
 * @returns { series: EChartsOption[] } - Returns an object with a new series array.
 *
 * @example
 *
 * const values = ['A', 'B'];
 * const seriesData = [{ name: 'test1' }, { name: 'test2' }];
 *
 * const result = composeSplitValueSeries(values, seriesData,
 *              (seriesName, splitValue) => `${seriesName}-${splitValue}`);
 *
 *  Expected result:
 *  {
 *     series: [
 *       { name: 'test1-A', id: 'A', datasetId: 'A' },
 *       { name: 'test1-B', id: 'B', datasetId: 'B' },
 * /      { name: 'test2-A', id: 'A', datasetId: 'A' },
 * /      { name: 'test2-B', id: 'B', datasetId: 'B' }
 * /    ]
 * / }
 */
export function composeSplitValueSeries(
    distinctValues: string[],
    series: EChartsOption[],
    colorGetter: (splitValue: string) => {} | { color: string },
    nameGetter?: (seriesName: string, splitValue: string) => string
): { series: EChartsOption[] } {
    const a = series.flatMap((s) => {
        const baseSeriesName = s['name'] as string;
        let seriesName: string = baseSeriesName ?? '';

        return distinctValues.map((splitValue) => {
            // if nameCallback is provided, use it to generate new name
            const name = nameGetter
                ? nameGetter(seriesName, splitValue)
                : `${seriesName}, ${splitValue}`;
            const color = colorGetter(splitValue);
            const newDistinctSeries = {
                ...s,
                ...{
                    id: name, //must be unique
                    name, // displayed on legend and tooltip unless overridden in tooltip formatter
                    datasetId: splitValue,
                    ...color,
                },
            };
            return newDistinctSeries;
        });
    });
    return { series: a };
}
